/**
 * Hold rules — pure decisions. The SeatInventory DO applies them inside one synchronous SQLite
 * transaction, so the decision and the write can never interleave with another request.
 */
import type { SeatState } from './seats';

export const HOLD_TTL_MS = 7 * 60_000; // "Koltukların 07:00 boyunca senin."
export const MAX_HOLD_LIFETIME_MS = 15 * 60_000; // re-selecting can't keep seats forever
export const PAYMENT_WINDOW_MS = 5 * 60_000; // 3-D Secure may take a while; extended once payment starts
export const ADMISSION_TTL_MS = 10 * 60_000; // waiting room: "Koltuk haritasında 10 dakikan var."

export type HoldStatus = 'active' | 'paying' | 'converted' | 'released' | 'expired';

export interface Hold {
  id: string;
  holder: string;
  seats: string[];
  status: HoldStatus;
  expiresAt: number;
  firstCreatedAt: number;
  createdAt: number;
  orderId: string | null;
}

export interface SeatView {
  state: SeatState;
  holdId: string | null;
  orderId: string | null;
}

export type HoldPlan =
  | { ok: true; seats: string[]; release: string[]; expiresAt: number; firstCreatedAt: number }
  | { ok: false; code: 'empty' | 'too_many' | 'unknown_seat' | 'conflict' | 'payment_in_progress'; conflicts?: string[] };

export const isLive = (h: Pick<Hold, 'status'>) => h.status === 'active' || h.status === 'paying';

export function holdExpiry(now: number, firstCreatedAt: number) {
  return Math.min(now + HOLD_TTL_MS, firstCreatedAt + MAX_HOLD_LIFETIME_MS);
}

/**
 * Plan a hold for `holder`. If the holder already has a live hold, the new hold replaces it:
 * seats they already hold are kept, seats no longer requested are released.
 */
export function planHold(args: {
  now: number;
  request: string[];
  max: number;
  seat: (id: string) => SeatView | undefined;
  existing?: Hold | null;
}): HoldPlan {
  const { now, max, seat, existing } = args;
  const req = [...new Set(args.request)];
  if (!req.length) return { ok: false, code: 'empty' };
  if (req.length > max) return { ok: false, code: 'too_many' };
  if (existing && existing.status === 'paying') return { ok: false, code: 'payment_in_progress' };
  const mine = existing && isLive(existing) && existing.expiresAt > now ? existing : null;
  const conflicts: string[] = [];
  for (const id of req) {
    const s = seat(id);
    if (!s) return { ok: false, code: 'unknown_seat', conflicts: [id] };
    const ownHeld = mine && s.state === 'held' && s.holdId === mine.id;
    if (s.state !== 'avail' && !ownHeld) conflicts.push(id);
  }
  if (conflicts.length) return { ok: false, code: 'conflict', conflicts };
  const first = mine ? mine.firstCreatedAt : now;
  const expiresAt = holdExpiry(now, first);
  if (expiresAt <= now) return { ok: false, code: 'conflict', conflicts: [] };
  const release = mine ? mine.seats.filter((id) => !req.includes(id)) : [];
  return { ok: true, seats: req, release, expiresAt, firstCreatedAt: first };
}

export type PaymentStart =
  | { ok: true; expiresAt: number }
  | { ok: false; code: 'not_found' | 'not_owner' | 'expired' | 'already_converted' | 'released' };

/** Payment begins: the hold becomes `paying` and is guaranteed to live through the 3-D Secure window. */
export function startPayment(h: Hold | null | undefined, holder: string, now: number): PaymentStart {
  if (!h) return { ok: false, code: 'not_found' };
  if (h.holder !== holder) return { ok: false, code: 'not_owner' };
  if (h.status === 'converted') return { ok: false, code: 'already_converted' };
  if (h.status === 'released') return { ok: false, code: 'released' };
  if (h.status === 'expired' || h.expiresAt <= now) return { ok: false, code: 'expired' };
  return { ok: true, expiresAt: Math.max(h.expiresAt, now + PAYMENT_WINDOW_MS) };
}

export type ConfirmDecision =
  | { kind: 'sell'; seats: string[] } // flip these seats to sold for the order
  | { kind: 'already'; seats: string[] } // idempotent replay — already sold to this order
  | { kind: 'conflict'; seats: string[] }; // someone else got (some of) the seats → refund

/**
 * Decide what a successful payment means for the seats. Called with the hold (which may have expired
 * meanwhile) and the live seat states. Never sells a seat that is held by another hold or sold to another order.
 */
export function decideConfirm(args: { hold: Hold; orderId: string; seat: (id: string) => SeatView | undefined }): ConfirmDecision {
  const { hold, orderId, seat } = args;
  const views = hold.seats.map((id) => [id, seat(id)] as const);
  if (views.every(([, v]) => v && v.state === 'sold' && v.orderId === orderId)) return { kind: 'already', seats: hold.seats };
  const conflicts = views
    .filter(([, v]) => {
      if (!v) return true;
      if (v.state === 'sold') return v.orderId !== orderId;
      if (v.state === 'held') return v.holdId !== hold.id;
      return v.state !== 'avail';
    })
    .map(([id]) => id);
  if (conflicts.length) return { kind: 'conflict', seats: conflicts };
  return { kind: 'sell', seats: hold.seats };
}

export const dueHolds = (holds: Hold[], now: number) => holds.filter((h) => isLive(h) && h.expiresAt <= now);
